import React, { Fragment } from 'react';
import { Col, Row, Button, Container, Card, CardHeader, CardBody } from 'reactstrap';

import { AddNoteButton, EditNoteModal, NotesList } from '../Notes';
import C from '../../C';
import KStatus from '../../base/data/KStatus';
import ActionMan from '../../plumbing/ActionMan';
import Login from '../../base/youagain';

import DataStore from '../../base/plumbing/DataStore';
import StyleBlock from '../../base/components/StyleBlock';
import { modifyPage } from '../../base/plumbing/glrouter';

import { GreenLogin, NoTagYet, TagDetails } from './GreenTagWidgets';
import GreenTagExporter from './GreenTagExporter';
import BulkUploader from './GreenTagBulkUploader';
import { TagHealth } from './GreenTagHealth';
import DevOnly from '../../base/components/DevOnly';
import GreenTagList from './GreenTagList';
import GreenTagEditor from './GreenTagEditor';

import '../../../style/GreenTagPage.less';


/** Left pane when editing or creating a new tag */
function EditPane({ tagId }) {
	const isNew = tagId === 'new';
	const type = C.TYPES.GreenTag;
	const path = DataStore.getDataPath({ type, status: KStatus.DRAFT, id: tagId }); // Path to draft copy of the tag

	// Back button action: Leave editor and discard local edits to draft
	const goBack = () => {
		modifyPage(isNew ? ['green'] : ['green', tagId]);
		DataStore.setValue(path, null);
	};

	return (
		<Card className="left-pane edit-pane">
			<CardHeader className="pane-header">
				{isNew ? 'Creating' : 'Editing'} Green Ad Tag
				<div role="button" className="back-chevron" onClick={goBack}>⏴</div>
			</CardHeader>
			<CardBody>
				<GreenTagEditor tagId={tagId} type={type} path={path} goBack={goBack} />
			</CardBody>
		</Card>
	);
}


/** Left pane when displaying, but not editing, a tag */
function ShowPane({ tagId }) {
	const itemSpec = { type: C.TYPES.GreenTag, status: KStatus.PUBLISHED, id: tagId };
	const pvTag = ActionMan.getDataItem(itemSpec);
	const tag = pvTag.value;

	let content;

	// Tag not loaded?
	if (!tag) {
		content = <NoTagYet pvTag={pvTag} tagId={tagId} />;
	} else {
		content = <>
			<TagDetails tag={tag} />
			<div>
				<Button color="primary" onClick={() => modifyPage(['green', tagId, 'edit'])}>Edit this tag</Button>
				<GreenTagExporter className="pull-right" tag={tag} />
			</div>
		</>;
	}

	return (
		<Card className="left-pane show-pane">
			<CardHeader className="pane-header">
				Your Green Ad Tag
				<div role="button" className="back-chevron" onClick={() => modifyPage(['green'])}>⏴</div>
			</CardHeader>
			<CardBody>
				{content}
				<hr/>
				<h4 className="mb-1 d-flex justify-content-between">
					Notes
					<AddNoteButton size="sm" item={tag} />
				</h4>
				<NotesList item={tag} />
				<EditNoteModal item={tag}/>
			</CardBody>
		</Card>
	);
}


/** Left pane when not focusing on a particular tag */
function IdlePane() {
	return (
		<Card className="left-pane idle-pane">
			<CardHeader className="pane-header">
				Green Ad Tag Generator
			</CardHeader>
			<CardBody>
				<p className="flex-row justify-content-between">
					<Button color="primary" onClick={() => modifyPage(['green', 'new', 'edit'])}>Create a new tag</Button>
					<BulkUploader />
				</p>
				<p>Or select an existing tag from the list on the right.</p>
			</CardBody>
		</Card>
	);
}


/**
 * The left pane, where individual tags are displayed, edited and exported.
 */
function ViewOrEdit() {
	const [/* #green */, tagId, mode] = DataStore.getValue(['location', 'path']);
	if (!tagId) return <IdlePane />;
	return (mode === 'edit') ? <EditPane tagId={tagId} /> : <ShowPane tagId={tagId} />;
}


/**
 * Generate, view, manage and export Green Ad Tags.
 * Paths:
 * /greentag ==> "select or create a tag" (always present behind view/edit pane which slides in/)
 * /greentag/$tagId ==> view tag
 * /greentag/$tagId/edit ==> edit tag
 */
function GreenTagPage() {
	if (!Login.isLoggedIn()) return <GreenLogin />;
	
	return <Container id="green-box">
		<DevOnly test bare><StyleBlock>{`#mainDiv .help-block.data-modified {display:none !important;}`}</StyleBlock></DevOnly>
		<Row>
			<Col sm="5" className="detail-view">
				<ViewOrEdit />
				<DevOnly className="my-2"><TagHealth /></DevOnly>
			</Col>
			<Col sm="7" className="list-view px-3 py-2">
				<GreenTagList />
			</Col>
		</Row>
	</Container>;
}


export default GreenTagPage;
